import React from 'react';
import { Link } from 'react-router-dom';
import { FiCheckCircle, FiAward, FiShield, FiArrowRight } from 'react-icons/fi';
import Button from '@components/common/Button';
import Card from '@components/common/Card';
import SectionHeader from '@components/common/SectionHeader';
import { PRODUCTS } from '@utils/constants';

/**
 * ProductsPreview Component
 * Homepage section showcasing featured products
 */
const ProductsPreview = () => {
  // Get featured product (first product)
  const featuredProduct = PRODUCTS[0];

  // Product features
  const features = [
    {
      icon: FiCheckCircle,
      title: 'Consistency',
      description: 'Uniform quality in every batch',
    },
    {
      icon: FiShield,
      title: 'Traceability',
      description: 'From farm to final product',
    },
    {
      icon: FiAward,
      title: 'Ethical Sourcing',
      description: 'Supporting local farmers',
    },
  ];

  // Certifications
  const certifications = [
    { name: 'UNBS', description: 'Uganda National Bureau of Standards' },
    { name: 'GMP', description: 'Good Manufacturing Practice' },
  ];

  return (
    <section className="section bg-bukawa-cream">
      <div className="container-custom">
        {/* Section Header */}
        <SectionHeader
          subtitle="Our Products"
          title="Premium White Maize Flour"
          description="High-quality, 100% pure white maize flour, ethically sourced from local farmers and processed to international standards."
          className="mb-16"
        />

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column - Product Image */}
          <div className="relative">
            {/* Main Product Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-large bg-white p-8">
              <img
                src={featuredProduct.image}
                alt={featuredProduct.name}
                className="w-full h-[500px] object-contain"
              />
              
              {/* Certification Badges */}
              <div className="absolute top-8 left-8 flex gap-3">
                {certifications.map((cert, index) => (
                  <div
                    key={index}
                    className="bg-bukawa-primary text-white px-4 py-2 rounded-full font-semibold text-sm shadow-lg"
                    title={cert.description}
                  >
                    {cert.name}
                  </div>
                ))}
              </div>

              {/* Made in Uganda Badge */}
              <div className="absolute bottom-8 right-8 bg-bukawa-secondary text-bukawa-primary-900 px-6 py-3 rounded-xl font-bold shadow-lg">
                Made in Uganda 🇺🇬
              </div>
            </div>

            {/* Decorative Element */}
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-bukawa-primary rounded-full opacity-10 blur-3xl" />
            <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-bukawa-secondary rounded-full opacity-10 blur-3xl" />
          </div>

          {/* Right Column - Product Details */}
          <div>
            {/* Product Name */}
            <h3 className="text-3xl md:text-4xl font-heading font-bold text-bukawa-primary mb-4">
              {featuredProduct.name}
            </h3>

            {/* Product Description */}
            <p className="text-lg text-bukawa-gray-700 mb-8 leading-relaxed">
              {featuredProduct.description}
            </p>

            {/* Features Grid */}
            <div className="grid gap-6 mb-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-4 bg-white rounded-xl hover:shadow-md transition-shadow"
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-bukawa-primary-50 rounded-lg flex items-center justify-center">
                      <Icon className="h-6 w-6 text-bukawa-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-bukawa-gray-900 mb-1">
                        {feature.title}
                      </h4>
                      <p className="text-sm text-bukawa-gray-600">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Product Specifications */}
            <Card className="mb-8 bg-bukawa-primary-50 border border-bukawa-primary-100">
              <h4 className="font-semibold text-bukawa-primary mb-4">
                Product Specifications
              </h4>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-bukawa-gray-600">
                    Ingredients:
                  </span>
                  <p className="font-medium text-bukawa-gray-900">
                    100% White Maize Flour
                  </p>
                </div>
                <div>
                  <span className="text-sm text-bukawa-gray-600">
                    Packaging:
                  </span>
                  <p className="font-medium text-bukawa-gray-900">
                    50kg Bags
                  </p>
                </div>
                <div>
                  <span className="text-sm text-bukawa-gray-600">
                    Storage:
                  </span>
                  <p className="font-medium text-bukawa-gray-900">
                    Cool, Dry Place
                  </p>
                </div>
                <div>
                  <span className="text-sm text-bukawa-gray-600">
                    Shelf Life:
                  </span>
                  <p className="font-medium text-bukawa-gray-900">
                    12 Months
                  </p>
                </div>
              </div>
            </Card>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                to="/products"
                variant="primary"
                size="lg"
                icon={<FiArrowRight />}
                iconPosition="right"
              >
                View All Products
              </Button>
              <Button
                to="/contact"
                variant="secondary"
                size="lg"
              >
                Request Quote
              </Button>
            </div>
          </div>
        </div>

        {/* Bottom Features Bar */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card hover className="text-center bg-white">
            <div className="w-16 h-16 bg-bukawa-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiCheckCircle className="h-8 w-8 text-bukawa-primary" />
            </div>
            <h4 className="font-semibold text-bukawa-gray-900 mb-2">
              Quality Guaranteed
            </h4>
            <p className="text-sm text-bukawa-gray-600">
              Every batch tested and certified to meet international standards
            </p>
          </Card>

          <Card hover className="text-center bg-white">
            <div className="w-16 h-16 bg-bukawa-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiShield className="h-8 w-8 text-bukawa-secondary-700" />
            </div>
            <h4 className="font-semibold text-bukawa-gray-900 mb-2">
              Fully Traceable
            </h4>
            <p className="text-sm text-bukawa-gray-600">
              From our cooperative farms to your table with complete transparency
            </p>
          </Card>

          <Card hover className="text-center bg-white">
            <div className="w-16 h-16 bg-bukawa-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiAward className="h-8 w-8 text-bukawa-primary" />
            </div>
            <h4 className="font-semibold text-bukawa-gray-900 mb-2">
              Ethically Sourced
            </h4>
            <p className="text-sm text-bukawa-gray-600">
              Supporting over 1,000 local farmers with fair practices
            </p>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ProductsPreview;